import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { 
  CheckCircleIcon,
  DocumentTextIcon,
  UserGroupIcon,
  BellIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import { PaymentStatus, getPaymentStatusTranslationKey } from '@tvia-ai/shared';

interface PaymentSuccessState {
  lawsuitId: string;
  lawsuitTitle: string;
  amount: number;
  currency: string;
}

const PaymentSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const state = location.state as PaymentSuccessState | null;

  useEffect(() => {
    // If no state data, redirect to dashboard
    if (!state) {
      navigate('/dashboard');
    }
  }, [state, navigate]);

  if (!state) {
    return null;
  }

  const nextSteps = [
    {
      icon: BellIcon,
      title: t('payment.success.step1Title', 'Stay Updated'),
      description: t('payment.success.step1Description', 'You\'ll receive email notifications about important case developments and milestones.')
    },
    {
      icon: DocumentTextIcon,
      title: t('payment.success.step2Title', 'Review Documents'),
      description: t('payment.success.step2Description', 'Access case documents and updates through your dashboard at any time.')
    },
    {
      icon: UserGroupIcon,
      title: t('payment.success.step3Title', 'Connect with Others'),
      description: t('payment.success.step3Description', 'Join discussions with other participants to stay informed about the case progress.')
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t('payment.success.title', 'Payment Successful')} - tvia.ai</title>
        <meta name="description" content={t('payment.success.description', 'Your payment has been processed successfully')} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Success Header */}
          <div className="text-center mb-12">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {t('payment.success.title', 'Payment Successful!')}
            </h1>
            <p className="text-lg text-gray-600">
              {t('payment.success.subtitle', 'You have successfully joined the lawsuit')}
            </p>
          </div>

          {/* Payment Details */}
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-6 py-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                {t('payment.success.paymentDetails', 'Payment Details')}
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    {t('lawsuit.title', 'Lawsuit')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 font-medium">
                    {state.lawsuitTitle}
                  </dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    {t('payment.amount', 'Amount Paid')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 font-medium">
                    ${state.amount} {state.currency}
                  </dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    {t('payment.success.transactionId', 'Transaction ID')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">
                    TXN-{state.lawsuitId}-{Date.now().toString().slice(-6)}
                  </dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    {t('payment.success.date', 'Date')}
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date().toLocaleDateString()}
                  </dd>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex">
                    <CheckCircleIcon className="h-5 w-5 text-green-400 mr-3" />
                    <div>
                      <h3 className="text-sm font-medium text-green-800">
                        {t(getPaymentStatusTranslationKey(PaymentStatus.Succeeded), 'Payment Confirmed')}
                      </h3>
                      <p className="mt-1 text-sm text-green-700">
                        {t('payment.success.confirmationMessage', 'Your participation in this lawsuit has been confirmed. You will receive a confirmation email shortly.')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-6 py-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">
                {t('payment.success.nextSteps', 'What Happens Next?')}
              </h2>
              
              <div className="space-y-6">
                {nextSteps.map((step, index) => (
                  <div key={index} className="flex">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-cyan-100">
                        <step.icon className="h-5 w-5 text-cyan-600" />
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        {step.title}
                      </h3>
                      <p className="mt-1 text-gray-600">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Important Information */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <h3 className="text-lg font-medium text-blue-900 mb-4">
              {t('payment.success.importantInfo', 'Important Information')}
            </h3>
            <ul className="space-y-2 text-sm text-blue-800">
              <li className="flex items-start">
                <span className="flex-shrink-0 h-1.5 w-1.5 rounded-full bg-blue-400 mt-2 mr-3"></span>
                {t('payment.success.info1', 'Keep this confirmation for your records')}
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 h-1.5 w-1.5 rounded-full bg-blue-400 mt-2 mr-3"></span>
                {t('payment.success.info2', 'Legal proceedings can take months or years to complete')}
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 h-1.5 w-1.5 rounded-full bg-blue-400 mt-2 mr-3"></span>
                {t('payment.success.info3', 'You will be notified of any major developments in the case')}
              </li>
              <li className="flex items-start">
                <span className="flex-shrink-0 h-1.5 w-1.5 rounded-full bg-blue-400 mt-2 mr-3"></span>
                {t('payment.success.info4', 'Settlement amounts, if any, will be distributed among all participants')}
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/dashboard"
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              <UserGroupIcon className="h-5 w-5 mr-2" />
              {t('navigation.dashboard', 'Go to Dashboard')}
            </Link>
            
            <Link
              to={`/lawsuit/${state.lawsuitId}`}
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              <DocumentTextIcon className="h-5 w-5 mr-2" />
              {t('lawsuit.viewDetails', 'View Lawsuit Details')}
            </Link>
            
            <Link
              to="/"
              className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              <HomeIcon className="h-5 w-5 mr-2" />
              {t('navigation.home', 'Home')}
            </Link>
          </div>

          {/* Support Contact */}
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-600">
              {t('payment.success.support', 'Need help? Contact our support team at')}{' '}
              <a href="mailto:support@tvia.ai" className="text-cyan-600 hover:text-cyan-700">
                support@tvia.ai
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PaymentSuccessPage; 